import * as Sentry from '@sentry/react';
import type { IPeerConnectionFactory } from '@playcastdotio/PeerCore';
import {
  GuestPeerSession,
  WebRtcOptimizationManager,
  SdpInspector,
  IceHealthGuard,
  type GuestPeerSessionCallbacks,
  type GuestRetryPolicy,
  type GuestConnectOptions,
  type BeaconE2eCongestionLevel,
  type BeaconE2eSnapshot,
  type EncodedFrameTap,
  type GuestStatsSample,
  type InputPhotonSummary,
  type RvfcVideoElement,
  type VisibilitySource,
} from '@playcastdotio/PeerCore';
import type { GuestBrowserCapability } from '@playcastdotio/PeerCore';
import type { PeerMetadata } from '@playcastdotio/Messaging';

export type RetryPolicy = GuestRetryPolicy;
export type ClientPeerCallbacks = GuestPeerSessionCallbacks;
export type { GuestConnectOptions };

/**
 * ClientPeerManager — thin marketing-demo wrapper around PeerCore GuestPeerSession.
 * Keeps the existing public API while shared guest media logic lives in PeerCore.
 */
export class ClientPeerManager {
  private readonly session: GuestPeerSession;

  /**
   * @param visibilitySource Where "is this page in front of the user" comes from. Optional so
   * every existing caller and every test keeps today's behaviour; supplied by the app so the
   * recovery budget actually stops while the tab is backgrounded, which is the whole point of
   * the fix — an option nothing ever passes is a fix that ships dead.
   */
  constructor(
    factory: IPeerConnectionFactory,
    callbacks: ClientPeerCallbacks,
    retryPolicy?: RetryPolicy,
    visibilitySource?: VisibilitySource,
    options: { capability?: GuestBrowserCapability; isMobile?: boolean; encodedFrameTap?: EncodedFrameTap } = {},
  ) {
    this.session = new GuestPeerSession({
      factory,
      callbacks,
      retryPolicy,
      ...(visibilitySource ? { visibilitySource } : {}),
      ...(options.capability ? { capability: options.capability } : {}),
      ...(options.isMobile !== undefined ? { isMobile: options.isMobile } : {}),
      ...(options.encodedFrameTap ? { encodedFrameTap: options.encodedFrameTap } : {}),
      optimizationManager: new WebRtcOptimizationManager(),
      sdpInspector: new SdpInspector(),
      iceHealth: IceHealthGuard,
      logger: {
        // Block body, not a concise one: an expression-bodied arrow *returns* the
        // console.log result, so esbuild cannot elide it under the `pure` annotation in
        // vite.config.ts and the informational channel would still run in production.
        // `warn`/`error` are deliberately left as-is — they are retained by design.
        info: (message) => { console.log(`[ClientPeerManager] ${message}`); },
        warn: (message) => console.warn(`[ClientPeerManager] ${message}`),
        error: (message, data) => console.error(`[ClientPeerManager] ${message}`, data ?? ''),
        breadcrumb: (message, level, data) => {
          Sentry.addBreadcrumb({
            category: 'webrtc',
            message,
            level,
            data,
          });
        },
      },
      enableBeaconE2eHooks: true,
    });
  }

  public preWarm(iceServers: RTCIceServer[]): void {
    this.session.preWarm(iceServers);
  }

  public hasPreWarmedConnection(): boolean {
    return this.session.hasPreWarmedConnection();
  }

  public connect(
    hostPeerId: string,
    iceServers: RTCIceServer[],
    metadata: PeerMetadata,
    connectOptions?: GuestConnectOptions,
  ): void {
    void this.session.connect(hostPeerId, iceServers, metadata, connectOptions).catch((error) => {
      console.error('[ClientPeerManager] Connect failed:', error);
    });
  }

  /** Async connect that awaits preferHardware decode before offer/answer. */
  public connectAsync(
    hostPeerId: string,
    iceServers: RTCIceServer[],
    metadata: PeerMetadata,
    connectOptions?: GuestConnectOptions,
  ): Promise<void> {
    return this.session.connect(hostPeerId, iceServers, metadata, connectOptions);
  }

  public get hasDecodedFrame(): boolean {
    return this.session.hasDecodedFrame;
  }

  public async handleRemoteSdp(sdp: RTCSessionDescriptionInit): Promise<void> {
    return this.session.handleRemoteSdp(sdp);
  }

  public handleRemoteIceCandidate(candidate: RTCIceCandidate): Promise<void> {
    return this.session.handleRemoteIceCandidate(candidate);
  }

  public send(data: string | ArrayBuffer, label?: string): boolean {
    return this.session.send(data, label);
  }

  public close(reason: string): void {
    this.session.close(reason);
  }

  public isConnected(): boolean {
    return this.session.isConnected();
  }

  public isReconnectInProgress(): boolean {
    return this.session.isReconnectInProgress();
  }

  public getRetryAttempt(): number {
    return this.session.getRetryAttempt();
  }

  public resetRetries(): void {
    this.session.resetRetries();
  }

  /**
   * Milliseconds left in the current recovery episode; 0 once spent, the full budget when no
   * episode is running. App layers read this so every one of them honours the SAME ten
   * seconds instead of each starting a fresh budget on top of the last.
   */
  public recoveryBudgetRemainingMs(): number {
    return this.session.recoveryBudgetRemainingMs();
  }

  /** End the episode, so a refresh gets a full budget rather than inheriting a spent one. */
  public resetRecoveryEpisode(): void {
    this.session.resetRecoveryEpisode();
  }

  public injectCongestion(level: BeaconE2eCongestionLevel): void {
    this.session.injectCongestion(level);
  }

  public getBeaconE2eSnapshot(): BeaconE2eSnapshot {
    return this.session.getBeaconE2eSnapshot();
  }

  /** Subscribe to guest stats samples for telemetry. Replaces any previous listener. */
  public onStatsSample(listener: ((sample: GuestStatsSample) => void) | undefined): void {
    this.session.onStatsSample(listener);
  }

  /** Ask the host for a keyframe. Returns false when it could not be sent. */
  public requestKeyframe(): boolean {
    return this.session.requestKeyframe();
  }

  /** Restart ICE in place. Resolves without throwing when there is no live connection. */
  public restartIce(): Promise<void> {
    return this.session.restartIce();
  }

  /**
   * Hand the remote `<video>` to the session so frame-presentation times can be measured.
   *
   * Without this the guest never registers `requestVideoFrameCallback`, which is the only
   * producer of the RTP-keyed decode hints the calibrated latency path consumes — so the
   * host reports `calibratedLatency: null` for the life of the session. Idempotent; pass
   * null on teardown.
   */
  public attachRemoteVideo(video: RvfcVideoElement | null): void {
    this.session.attachRemoteVideo(video);
  }

  public getPresentationEvidence(): ReturnType<GuestPeerSession['getPresentationEvidence']> {
    return this.session.getPresentationEvidence();
  }

  public getRecoveryActionArbiter(): ReturnType<GuestPeerSession['getRecoveryActionArbiter']> {
    return this.session.getRecoveryActionArbiter();
  }

  /**
   * Claim a sequence number for an input about to be sent, or null when this one falls
   * inside the sampling interval and should go out untagged.
   */
  public sampleInputForPhoton(nowMs?: number): number | null {
    return this.session.sampleInputForPhoton(nowMs);
  }

  /** Rolling input-to-photon latency: input event to the frame that could answer it. */
  public inputPhotonSummary(): InputPhotonSummary {
    return this.session.inputPhotonSummary();
  }
}
